import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { EditPropertyDialog } from '@/components/properties/EditPropertyDialog';
import { ArchivePropertyDialog } from '@/components/properties/ArchivePropertyDialog';
import { DeletePropertyDialog } from '@/components/properties/DeletePropertyDialog';
import { PlacePropertyPin } from '@/components/properties/PlacePropertyPin';
import { PropertyLocationMap } from '@/components/properties/PropertyLocationMap';
import { PropertyPartiesSection } from '@/components/properties/PropertyPartiesSection';
import { useAccount } from '@/api/useAccount';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { formatDate } from '@/lib/format';
import { byServiceDateDescending, serviceDate } from '@/routes/customers/useCustomers';
import { NotFound } from './NotFound';
import {
  PropertyNotFoundError,
  PropertyNotPermittedError,
  toNumericCoordinate,
  useGeocodeProperty,
  useProperty,
  usePropertyJobs,
  useSetPropertyLocation,
} from './properties/useProperty';

const RECENT_JOBS_SHOWN = 5;

/** Five decimals is about a metre — enough to recognise a pin, not a survey. */
function formatCoordinate(value: number | string | null | undefined): string {
  const n = toNumericCoordinate(value);
  return n == null ? '' : n.toFixed(5);
}

/**
 * The retry the AC asks for (M1-23). Announces the NEW status rather than
 * "retried": a lookup that finds nothing again is a fact the office needs to
 * hear, and it is the common case for the address that failed the first time.
 */
function RetryGeocodeButton({
  propertyId,
  line1,
  onOutcome,
}: {
  propertyId: string;
  line1: string;
  onOutcome: (announcement: string) => void;
}) {
  const { t } = useTranslation();
  const geocode = useGeocodeProperty(propertyId);

  return (
    <div className="mt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={geocode.isPending}
        onClick={() =>
          geocode.mutate(undefined, {
            onSuccess: (updated) => {
              const key =
                updated.geocodeStatus === 'Geocoded'
                  ? 'retriedGeocoded'
                  : updated.geocodeStatus === 'NoMatch'
                    ? 'retriedNoMatch'
                    : 'retriedFailed';
              onOutcome(t(`propertyDetail.location.${key}`, { line1 }));
            },
          })
        }
      >
        {geocode.isPending
          ? t('propertyDetail.location.retrying')
          : t('propertyDetail.location.retry')}
      </Button>
      {geocode.isError ? (
        <p role="alert" className="mt-2 text-sm text-[var(--color-destructive)]">
          {t('propertyDetail.location.retryError')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One property: the address, access details and notes a technician needs
 * standing at the door, its own recent jobs, and — from M1-05's scope
 * extension — the edit/archive/delete actions M1-02 shipped API-only.
 *
 * <b>Reachable by a Technician, not just office staff.</b> The AC's own frame
 * is "as a technician, when I arrive" — but `customers.read` (the permission
 * the customer page and property list are gated on) is not a permission a
 * Technician holds. The route gates on `jobs.work` instead (every role holds
 * it) and the API narrows WHICH property a Technician-only caller may open to
 * one they have a job at (api/CONVENTIONS.md's M1-05 section) — same shape as
 * M1-13's job page. They reach this page from their own job's detail link;
 * there is still no property list or search they can browse.
 *
 * <b>Two sources for "past jobs", picked by permission, not by this page.</b>
 * A caller with `jobs.read.all` gets the full history via `usePropertyJobs`
 * (same `GET /jobs?propertyId=` office screens already use) and can expand
 * past the first five. A Technician-only caller has neither the endpoint nor
 * a way to ask for more — they see exactly the bounded `recentJobs` the
 * property response already carries, which is what OD-09 decided a
 * technician needs on arrival anyway.
 */
export function PropertyDetail() {
  const { t } = useTranslation();
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const hasPermission = useHasPermission();
  const canReadAllJobs = hasPermission(Permissions.readAllJobs);
  const canManage = hasPermission(Permissions.manageCustomers);
  const canReadCustomers = hasPermission(Permissions.readCustomers);

  const account = useAccount();
  const timeZone = account.data?.timezone;
  const property = useProperty(propertyId);
  const fullHistory = usePropertyJobs(propertyId, canReadAllJobs);
  const setLocation = useSetPropertyLocation(propertyId ?? '');

  const [dialog, setDialog] = useState<'edit' | 'archive' | 'delete' | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [showAllJobs, setShowAllJobs] = useState(false);

  if (property.error instanceof PropertyNotFoundError) {
    return <NotFound />;
  }

  if (property.isPending) {
    return <LoadingState label={t('propertyDetail.loading')} />;
  }

  if (property.error instanceof PropertyNotPermittedError) {
    return (
      <EmptyState
        title={t('propertyDetail.notPermittedTitle')}
        description={t('propertyDetail.notPermittedDescription')}
      />
    );
  }

  if (property.isError) {
    return (
      <ErrorState
        title={t('propertyDetail.loadErrorTitle')}
        description={t('propertyDetail.loadErrorDescription')}
        onRetry={() => void property.refetch()}
      />
    );
  }

  const detail = property.data;
  const record = detail.property;
  // The full-history query is the source of truth once it is available (it
  // is what "show more" expands into); the embedded, bounded list is a
  // Technician-only caller's ONLY source, and is always what loads first.
  const jobsSource = canReadAllJobs ? (fullHistory.data ?? detail.recentJobs) : detail.recentJobs;
  const orderedJobs = byServiceDateDescending(jobsSource);
  const visibleJobs = showAllJobs ? orderedJobs : orderedJobs.slice(0, RECENT_JOBS_SHOWN);
  const hiddenCount = orderedJobs.length - visibleJobs.length;

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        {canReadCustomers ? (
          <p className="text-sm">
            <Link
              to={`/customers/${record.customerId}`}
              className="text-[var(--color-muted-foreground)] underline-offset-2 hover:underline focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
            >
              {t('propertyDetail.backToCustomer')}
            </Link>
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {record.address.line1}
            {record.unit ? `, ${record.unit}` : ''}
          </h1>
          {record.status === 'Archived' ? (
            <span className="rounded-full border border-[var(--color-destructive)] px-2 py-0.5 text-xs font-medium text-[var(--color-destructive)]">
              {t('propertyDetail.archived')}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {record.address.city}, {record.address.state} {record.address.postalCode}
        </p>
      </div>

      {/* ---- Quick actions ------------------------------------------------ */}
      {canManage ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setDialog('edit')}>
            {t('propertyDetail.editProperty.action')}
          </Button>
          {record.status !== 'Archived' ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('archive')}>
              {t('propertyDetail.archiveProperty.action')}
            </Button>
          ) : null}
          <Button type="button" variant="outline" size="sm" onClick={() => setDialog('delete')}>
            {t('propertyDetail.deleteProperty.action')}
          </Button>
        </div>
      ) : null}

      {/* ---- What a tech needs at the door --------------------------------- */}
      {/* The AC's own framing: prominent, not tucked into a details list. */}
      {record.gateCode || record.accessNotes ? (
        <div className="rounded-lg border-2 border-[var(--color-primary)] p-4">
          <h2 className="text-base font-semibold">{t('propertyDetail.atTheDoorHeading')}</h2>
          <dl className="mt-2 space-y-2">
            {record.gateCode ? (
              <div>
                <dt className="text-xs text-[var(--color-muted-foreground)]">
                  {t('propertyDetail.gateCode')}
                </dt>
                <dd className="text-lg font-semibold">{record.gateCode}</dd>
              </div>
            ) : null}
            {record.accessNotes ? (
              <div>
                <dt className="text-xs text-[var(--color-muted-foreground)]">
                  {t('propertyDetail.accessNotes')}
                </dt>
                <dd className="whitespace-pre-line">{record.accessNotes}</dd>
              </div>
            ) : null}
          </dl>
        </div>
      ) : null}

      {/* ---- Property notes (separate from access notes) ------------------- */}
      {record.notes ? (
        <div className="rounded-lg border p-4">
          <h2 className="text-base font-semibold">{t('propertyDetail.notesHeading')}</h2>
          <p className="mt-2 text-sm whitespace-pre-line">{record.notes}</p>
        </div>
      ) : null}

      {/* ---- Parties (M1-25) ------------------------------------------------ */}
      <PropertyPartiesSection propertyId={propertyId ?? ''} parties={record.parties} canManage={canManage} />

      {/* ---- Location (M1-23) ---------------------------------------------- */}
      {/* Words, not a coloured dot: the status is the fact. M1-26 draws the map. */}
      <div className="rounded-lg border p-4" data-testid="property-location">
        <h2 className="text-base font-semibold">{t('propertyDetail.location.heading')}</h2>
        <p className="mt-2 text-sm" data-testid="property-location-status">
          {record.geocodeStatus === 'Geocoded'
            ? t('propertyDetail.location.geocoded', {
                latitude: formatCoordinate(record.latitude),
                longitude: formatCoordinate(record.longitude),
              })
            : record.geocodeStatus === 'NoMatch'
              ? t('propertyDetail.location.noMatch')
              : record.geocodeStatus === 'Failed'
                ? t('propertyDetail.location.failed')
                : t('propertyDetail.location.pending')}
        </p>
        {record.geocodeDetail ? (
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {record.geocodeDetail}
            {record.manuallyAdjusted ? ` ${t('propertyDetail.location.manuallyAdjusted')}` : ''}
          </p>
        ) : null}
        {/* Also offered on a manually placed pin (M1-26): a drag sets the
            status to Geocoded, so without the second clause the explicit
            re-geocode — the only thing that clears manuallyAdjusted — would
            have no trigger anywhere in the app, and an address corrected after
            a drag could never win its pin back. */}
        {canManage &&
        propertyId &&
        (record.geocodeStatus !== 'Geocoded' || record.manuallyAdjusted) ? (
          <RetryGeocodeButton
            propertyId={propertyId}
            line1={record.address.line1}
            onOutcome={setAnnouncement}
          />
        ) : null}
        {/*
            M1-36: the property has no pin at all. `PUT .../location` accepted
            this case from the day M1-26 shipped and simply had no caller —
            until now the office's only route to a pin was editing the stored
            address until the provider happened to match something.

            ⚠️ Gated on canManage, so somebody who can only READ still sees no
            map for an unmatched property, exactly as before. Offering a
            placement affordance to a viewer who cannot save it would be a
            button that always fails.
        */}
        {canManage &&
        propertyId &&
        !(
          record.geocodeStatus === 'Geocoded' &&
          toNumericCoordinate(record.latitude) != null &&
          toNumericCoordinate(record.longitude) != null
        ) ? (
          <PlacePropertyPin
            propertyId={propertyId}
            onPlaced={() => setAnnouncement(t('propertyDetail.pinPlaced'))}
          />
        ) : null}
        {record.geocodeStatus === 'Geocoded' &&
        toNumericCoordinate(record.latitude) != null &&
        toNumericCoordinate(record.longitude) != null ? (
          <PropertyLocationMap
            latitude={toNumericCoordinate(record.latitude)!}
            longitude={toNumericCoordinate(record.longitude)!}
            editable={canManage}
            onCorrect={(latitude, longitude) => setLocation.mutateAsync({ latitude, longitude })}
          />
        ) : null}
      </div>

      {/* ---- Past jobs ------------------------------------------------------ */}
      <div className="space-y-3">
        <h2 className="text-base font-semibold">{t('propertyDetail.pastJobsHeading')}</h2>

        {(canReadAllJobs ? fullHistory.isPending : false) ? (
          <LoadingState label={t('propertyDetail.pastJobsLoading')} />
        ) : canReadAllJobs && fullHistory.isError ? (
          <ErrorState
            title={t('propertyDetail.pastJobsErrorTitle')}
            onRetry={() => void fullHistory.refetch()}
          />
        ) : orderedJobs.length === 0 ? (
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('propertyDetail.noPastJobs')}
          </p>
        ) : (
          <>
            <ul className="space-y-2">
              {visibleJobs.map((job) => {
                const when = serviceDate(job);
                return (
                  <li key={job.id} data-testid="past-job-card">
                    <Link
                      to={`/jobs/${job.id}`}
                      className="block rounded-lg border p-3 text-sm hover:bg-[var(--color-muted)] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium">
                          {job.jobType ?? t('propertyDetail.noJobType')}
                        </span>
                        <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                          {t(`common.jobStatus.${job.status}`, { defaultValue: job.status })}
                        </span>
                      </div>
                      <span className="block text-xs text-[var(--color-muted-foreground)]">
                        {job.description}
                      </span>
                      <span className="mt-1 block text-xs text-[var(--color-muted-foreground)]">
                        {when
                          ? formatDate(when, { dateStyle: 'medium', timeZone })
                          : t('propertyDetail.notScheduled')}
                        {' · '}
                        {job.assignedUserName ?? t('propertyDetail.unassigned')}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            {hiddenCount > 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowAllJobs(true)}
              >
                {t('propertyDetail.showMoreJobs', { count: hiddenCount })}
              </Button>
            ) : null}
          </>
        )}
      </div>

      <p role="status" aria-live="polite" className="sr-only" data-testid="property-announcement">
        {announcement}
      </p>

      {dialog === 'edit' ? (
        <EditPropertyDialog
          property={record}
          onClose={() => setDialog(null)}
          onSaved={() =>
            setAnnouncement(t('propertyDetail.editProperty.saved', { line1: record.address.line1 }))
          }
        />
      ) : null}

      {dialog === 'archive' && propertyId ? (
        <ArchivePropertyDialog
          propertyId={propertyId}
          propertyLine1={record.address.line1}
          onClose={() => setDialog(null)}
          onArchived={() =>
            setAnnouncement(
              t('propertyDetail.archiveProperty.archived', { line1: record.address.line1 }),
            )
          }
        />
      ) : null}

      {dialog === 'delete' && propertyId ? (
        <DeletePropertyDialog
          propertyId={propertyId}
          propertyLine1={record.address.line1}
          onClose={() => setDialog(null)}
          onDeleted={() => navigate(`/customers/${record.customerId}`)}
        />
      ) : null}
    </section>
  );
}
